import { Briefcase, Check, FileText, Loader2, Upload } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { MemoryField } from '../../shared/memory.ts';
import { RESUME_MAX_BYTES } from '../../shared/setup.ts';
import { api } from '../lib/api.ts';
import { cn } from '../lib/cn.ts';
import { useMemory } from '../shell/useMemory.tsx';

// No reminders step while nothing sends them (FOLLOWUPS: "Reminders are captured but never sent"). Saved preferences
// and PUT /api/setup/nudges stay; the form was removed in the PR "Hide reminders until something sends them".
export const SETUP_STEPS = ['resume', 'interview'] as const;
const STEPS = SETUP_STEPS;
const STEP_NAMES: Record<(typeof SETUP_STEPS)[number], string> = {
  resume: 'Resume',
  interview: 'Interview details',
};

/**
 * Screen 2, after call 1: the resume and interview details, which give the next call real context. Finishing the
 * last step moves the journey to `context_ready`.
 */
export function SetupFlow({ onFinished, initialStep = 0 }: { onFinished?: () => void; initialStep?: number }) {
  const { set } = useMemory();
  const [step, setStep] = useState(initialStep);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const next = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      return;
    }
    setFinishing(true);
    setError(null);
    try {
      set((await api.finishSetup()).memory);
      onFinished?.();
    } catch {
      setError('Could not finish setup. Try again.');
    } finally {
      setFinishing(false);
    }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <section data-testid="setup" data-step={STEPS[step]} className="w-full space-y-5 text-left">
      <ol className="space-y-3" aria-label="Your journey">
        <TimelineItem state="done" title="Getting to know you" />
        {/* The sub-step lives in the journey line itself, so there's one progress indicator, not two. */}
        <TimelineItem
          state="current"
          title="Your context"
          detail={`${STEP_NAMES[STEPS[step]!]} · ${step + 1} of ${STEPS.length}`}
        />
        <TimelineItem state="upcoming" title="First practice call" />
      </ol>

      <div className="card space-y-5 ring-1 ring-fg/10">
        {step === 0 ? <ResumeStep onNext={next} /> : null}
        {step === 1 ? <InterviewStep onNext={next} onBack={back} finishing={finishing} /> : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Form state seeded from memory. Fields the user hasn't touched follow later memory changes (a correction made in
 * Profile meanwhile, a call's post-call update), so saving never writes stale values back; touched fields keep
 * what the user typed.
 */
function useSeededForm<T extends Record<string, string>>(seed: T) {
  const [values, setValues] = useState(seed);
  const touched = useRef(new Set<keyof T>());
  const seedKey = JSON.stringify(seed);
  // biome-ignore lint/correctness/useExhaustiveDependencies: seedKey is the value-equality stand-in for seed
  useEffect(() => {
    setValues((current) => {
      const next = { ...current };
      for (const k of Object.keys(seed) as Array<keyof T>) if (!touched.current.has(k)) next[k] = seed[k];
      return next;
    });
  }, [seedKey]);
  const setField = (key: keyof T, value: string) => {
    touched.current.add(key);
    setValues((v) => ({ ...v, [key]: value }));
  };
  return [values, setField] as const;
}

function TimelineItem({
  state,
  title,
  detail,
}: {
  state: 'done' | 'current' | 'upcoming';
  title: string;
  detail?: string;
}) {
  return (
    <li className="flex items-start gap-3" aria-current={state === 'current' ? 'step' : undefined}>
      <span
        className={cn(
          'mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-xs',
          state === 'done' && 'bg-good/15 text-good',
          state === 'current' && 'bg-accent/15 ring-1 ring-accent/60',
          state === 'upcoming' && 'ring-1 ring-fg/15',
        )}
      >
        {state === 'done' ? <Check className="size-3.5" aria-label="done" /> : null}
        {state === 'current' ? <span className="size-2 rounded-full bg-accent" /> : null}
      </span>
      <span>
        <span className={cn('block text-[15px]', state === 'upcoming' ? 'text-subtle' : 'text-fg/90')}>{title}</span>
        {detail ? <span className="block text-xs text-subtle">{detail}</span> : null}
      </span>
    </li>
  );
}

function StepHeader({ icon, title, hint }: { icon: ReactNode; title: string; hint: string }) {
  return (
    <header className="flex gap-3">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent">{icon}</span>
      <span>
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        <p className="text-sm text-subtle">{hint}</p>
      </span>
    </header>
  );
}

function StepActions({
  primary,
  onPrimary,
  primaryDisabled,
  busy,
  onSkip,
  onBack,
  backLabel = 'Back',
}: {
  primary?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  busy?: boolean;
  onSkip?: () => void;
  onBack?: () => void;
  backLabel?: string;
}) {
  return (
    <div className="flex items-center gap-2 pt-1">
      {/* Not while busy: a save that lands after Back would run the step's next from another step. */}
      {onBack ? (
        <button
          type="button"
          data-testid="setup-back"
          onClick={onBack}
          disabled={busy}
          className="px-2 py-2.5 text-sm text-subtle hover:text-fg disabled:opacity-40"
        >
          {backLabel}
        </button>
      ) : null}
      <span className="flex-1" />
      {onSkip ? (
        <button type="button" data-testid="setup-skip" onClick={onSkip} disabled={busy} className="btn-quiet">
          Skip for now
        </button>
      ) : null}
      {primary ? (
        <button
          type="button"
          data-testid="setup-next"
          onClick={onPrimary}
          disabled={primaryDisabled || busy}
          className="inline-flex items-center gap-2 rounded-full bg-fg px-5 py-2.5 text-[15px] font-semibold text-bg transition disabled:opacity-40"
        >
          {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {primary}
        </button>
      ) : null}
    </div>
  );
}

const inputClass =
  'w-full rounded-xl bg-fg/5 px-3 py-2.5 text-[15px] text-fg ring-1 ring-fg/15 placeholder:text-subtle focus:outline-none focus:ring-accent/50';

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm text-subtle">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Upload, show and remove the resume summary. Used by the setup step and the Profile tab. */
export function ResumeUpload({ onReading }: { onReading?: (reading: boolean) => void }) {
  const { memory, set } = useMemory();
  const [reading, setReadingState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const resume = memory.resume;
  const setReading = (r: boolean) => {
    setReadingState(r);
    onReading?.(r);
  };

  const onFile = async (file: File) => {
    setError(null);
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please choose a PDF.');
      return;
    }
    if (file.size > RESUME_MAX_BYTES) {
      setError('That file is over 5 MB. Try a smaller PDF.');
      return;
    }
    setReading(true);
    try {
      const res = await api.uploadResume(file);
      if ('error' in res) setError(res.error);
      else set(res.memory);
    } catch {
      setError('Upload failed. Check your connection and try again.');
    } finally {
      setReading(false);
    }
  };

  return (
    <div className="space-y-3">
      {resume && !reading ? (
        <div data-testid="resume-summary" className="space-y-3 rounded-2xl bg-fg/[0.03] p-4 ring-1 ring-fg/10">
          <p className="text-[15px] text-fg/85">{resume.summary}</p>
          {resume.skills.length ? (
            <div className="flex flex-wrap gap-1.5">
              {resume.skills.slice(0, 8).map((s) => (
                <span key={s} className="rounded-full bg-fg/5 px-2.5 py-0.5 text-xs text-muted">
                  {s}
                </span>
              ))}
            </div>
          ) : null}
          <div className="flex gap-4 text-sm">
            <button type="button" onClick={() => input.current?.click()} className="text-accent hover:underline">
              Replace
            </button>
            <button
              type="button"
              data-testid="resume-remove"
              onClick={() =>
                void api.removeResume().then(
                  (r) => set(r.memory),
                  () => setError('Could not remove it. Try again.'),
                )
              }
              className="text-subtle hover:text-bad"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="resume-pick"
          onClick={() => input.current?.click()}
          disabled={reading}
          className="flex w-full flex-col items-center gap-2 rounded-2xl border border-fg/15 border-dashed px-4 py-8 text-sm text-muted transition hover:border-accent/40 hover:text-fg/85"
        >
          {reading ? (
            <Loader2 className="size-5 animate-spin" aria-hidden />
          ) : (
            <Upload className="size-5" aria-hidden />
          )}
          {reading ? 'Reading your resume…' : 'Choose a PDF (up to 5 MB)'}
        </button>
      )}
      <input
        ref={input}
        data-testid="resume-input"
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void onFile(file);
        }}
      />
      {error ? (
        <p role="alert" data-testid="resume-error" className="text-sm text-bad">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ResumeStep({ onNext }: { onNext: () => void }) {
  const { memory } = useMemory();
  const [reading, setReading] = useState(false);
  return (
    <div data-testid="setup-resume" className="space-y-4">
      <StepHeader
        icon={<FileText className="size-5" />}
        title="Your resume"
        hint="I'll tailor questions to your real experience. I keep a short summary, not the file."
      />
      <ResumeUpload onReading={setReading} />
      <StepActions
        primary={memory.resume ? 'Continue' : undefined}
        onPrimary={onNext}
        onSkip={memory.resume ? undefined : onNext}
        busy={reading}
      />
    </div>
  );
}

function InterviewStep({ onNext, onBack, finishing }: { onNext: () => void; onBack: () => void; finishing: boolean }) {
  const { memory, set } = useMemory();
  const value = (f: MemoryField) => memory.fields[f]?.value ?? '';
  const [form, setField] = useSeededForm({
    role: value('interview.role'),
    company: value('interview.company'),
    date: value('interview.date'),
    round: value('interview.round'),
  });
  const [jd, setJd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedJd = value('interview.jdSummary');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      set((await api.saveInterview({ ...form, ...(jd.trim() ? { jd } : {}) })).memory);
      onNext();
    } catch {
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };
  const bind = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: { target: { value: string } }) => setField(key, e.target.value),
    className: inputClass,
    id: `interview-${key}`,
    'data-testid': `interview-${key}`,
  });

  return (
    <div data-testid="setup-interview" className="space-y-4">
      <StepHeader
        icon={<Briefcase className="size-5" />}
        title="Your interview"
        hint="Here's what I heard on our call. Fix anything I got wrong."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Role" id="interview-role">
          <input {...bind('role')} maxLength={100} placeholder="e.g. Product Manager" />
        </Field>
        <Field label="Company" id="interview-company">
          <input {...bind('company')} maxLength={100} placeholder="e.g. Flipkart" />
        </Field>
        <Field label="Interview date" id="interview-date">
          <input {...bind('date')} maxLength={40} placeholder="e.g. 15 October" />
        </Field>
        <Field label="Round" id="interview-round">
          <input {...bind('round')} maxLength={100} placeholder="e.g. Final, behavioural" />
        </Field>
      </div>
      <Field label="Job description (optional)" id="interview-jd">
        <textarea
          id="interview-jd"
          data-testid="interview-jd"
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          maxLength={20_000}
          rows={4}
          placeholder={
            savedJd ? 'Paste a new one to replace what I have.' : "Paste it here. I'll keep a short summary."
          }
          className={cn(inputClass, 'resize-none')}
        />
      </Field>
      {savedJd ? (
        <p data-testid="jd-summary" className="text-xs text-subtle">
          I have: {savedJd.length > 160 ? `${savedJd.slice(0, 160)}…` : savedJd}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      ) : null}
      <StepActions primary="Finish" onPrimary={() => void save()} busy={saving || finishing} onBack={onBack} />
    </div>
  );
}
